// src/app/api/workspaceApi.ts
//
// In-app workspaces: a child's Scratch projects and code projects, built
// inside the lessons (backend routes/learnWorkspaces.js).

import { baseApi } from "./baseApi";

export type WorkspaceKind = "scratch" | "web" | "react" | "native";

/** web, react and native projects are files edited in the code editor */
export const isCodeKind = (k: WorkspaceKind | null | undefined): k is "web" | "react" | "native" =>
  k === "web" || k === "react" || k === "native";

export interface ProjectFile {
  path: string;
  content: string;
  /** "dataurl" for pictures */
  encoding?: "text" | "dataurl";
}

export interface ProjectSummary {
  id: string;
  kind: WorkspaceKind;
  name: string;
  revision: number;
  bytes: number;
  /** false for a Scratch project that has never been saved */
  saved: boolean;
  lastLessonKey: string | null;
  lastOpenedAt: string;
  updatedAt: string;
  createdAt: string;
}

export interface ProjectDetail extends ProjectSummary {
  files: ProjectFile[] | null;
  sb3Url: string | null;
}

interface Ok<T> {
  success: boolean;
  message?: string;
  data: T;
}

export const KIND_LABEL: Record<WorkspaceKind, { tool: string; project: string }> = {
  scratch: { tool: "Scratch", project: "Scratch project" },
  web: { tool: "the code editor", project: "code project" },
  react: { tool: "the code editor", project: "React app" },
  native: { tool: "the code editor", project: "React Native app" },
};

export const workspaceApi = baseApi.enhanceEndpoints({ addTagTypes: ["Workspace"] }).injectEndpoints({
  endpoints: (b) => ({
    getProjects: b.query<Ok<ProjectSummary[]>, { childId: string; kind?: WorkspaceKind }>({
      query: ({ childId, kind }) => `/learn/${childId}/workspaces${kind ? `?kind=${kind}` : ""}`,
      providesTags: (_r, _e, a) => [{ type: "Workspace", id: a.childId }],
    }),
    // A mutation, so every open fetches the latest save
    openProject: b.mutation<Ok<ProjectDetail>, { childId: string; id: string }>({
      query: ({ childId, id }) => ({ url: `/learn/${childId}/workspaces/${id}`, method: "GET" }),
    }),
    createProject: b.mutation<
      Ok<ProjectSummary & { files: ProjectFile[] | null }>,
      { childId: string; kind: WorkspaceKind; name?: string; lessonKey?: string; blank?: boolean }
    >({
      query: ({ childId, ...body }) => ({ url: `/learn/${childId}/workspaces`, method: "POST", body }),
      invalidatesTags: (_r, _e, a) => [{ type: "Workspace", id: a.childId }],
    }),
    saveProject: b.mutation<
      Ok<ProjectSummary>,
      {
        childId: string;
        id: string;
        baseRevision: number;
        name?: string;
        files?: ProjectFile[];
        sb3Key?: string;
        lessonKey?: string;
        force?: boolean;
      }
    >({
      query: ({ childId, id, ...body }) => ({ url: `/learn/${childId}/workspaces/${id}`, method: "PUT", body }),
      invalidatesTags: (_r, _e, a) => [{ type: "Workspace", id: a.childId }],
    }),
    getSb3UploadUrl: b.mutation<Ok<{ uploadUrl: string; key: string }>, { childId: string; id: string; sizeBytes: number }>({
      query: ({ childId, id, sizeBytes }) => ({
        url: `/learn/${childId}/workspaces/${id}/sb3-upload-url`,
        method: "POST",
        body: { sizeBytes },
      }),
    }),
    deleteProject: b.mutation<Ok<null>, { childId: string; id: string }>({
      query: ({ childId, id }) => ({ url: `/learn/${childId}/workspaces/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, a) => [{ type: "Workspace", id: a.childId }],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useOpenProjectMutation,
  useCreateProjectMutation,
  useSaveProjectMutation,
  useGetSb3UploadUrlMutation,
  useDeleteProjectMutation,
} = workspaceApi;
